

import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { cn } from "@/lib/utils"

type TabGroupSize = "sm" | "md" | "lg"
type TabGroupColor = "brand-light-red" | "brand-blue"

interface TabGroupContextValue {
  size: TabGroupSize
  color: TabGroupColor
  fullWidth: boolean
}

const TabGroupContext = React.createContext<TabGroupContextValue>({
  size: "md",
  color: "brand-light-red",
  fullWidth: false,
})

interface TabGroupProps extends React.ComponentProps<typeof TabsPrimitive.Root> {
  size?: TabGroupSize
  color?: TabGroupColor
  fullWidth?: boolean
}

function TabGroup({ className, size = "md", color = "brand-light-red", fullWidth = false, ...props }: TabGroupProps) {
  return (
    <TabGroupContext.Provider value={{ size, color, fullWidth }}>
      <TabsPrimitive.Root className={cn(
        `flex flex-col gap-2`,
        fullWidth ? "w-full" : "w-fit",
        className,
      )} 
      {...props} />
    </TabGroupContext.Provider>
  )
}

function TabGroupList({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
  const { size, color, fullWidth } = React.useContext(TabGroupContext)

  const sizeClasses = {
    sm: "h-8 text-sm p-0.5",
    md: "h-10 text-base p-0.5",
    lg: "h-12 text-lg p-0.5",
  }

  const colorClasses = {
    "brand-light-red": "border-brand-light-red",
    "brand-blue": "border-gray-200",
  }

  const borderColor = color === 'brand-blue'
    ? `border-gray-200`
    : `border-${color}`

  return (
    <TabsPrimitive.List
      className={cn(
        `inline-flex items-center justify-center rounded-lg border-1 ${borderColor} border`,
        fullWidth ? "w-full" : "w-fit",
        sizeClasses[size],
        colorClasses[color],
        className,
      )}
      {...props}
    />
  )
}

function TabGroupTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const { size, color, fullWidth } = React.useContext(TabGroupContext)

  const sizeClasses = {
    sm: "px-3 py-1 text-sm",
    md: "px-4 py-1.5 text-base",
    lg: "px-6 py-2 text-lg",
  }

  const colorClasses = {
    "brand-light-red": "data-[state=active]:text-[#FF7171]",
    "brand-blue": "data-[state=active]:text-white data-[state=active]:bg-brand-blue",
  }

  return (
    <TabsPrimitive.Trigger
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium transition-colors",
        "text-gray-700 hover:text-gray-900",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-1",
        "disabled:pointer-events-none disabled:opacity-50",
        fullWidth && "flex-1",
        sizeClasses[size],
        colorClasses[color],
        className,
      )}
      {...props}
    />
  )
}

function TabGroupContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return <TabsPrimitive.Content className={cn("flex-1 outline-none", className)} {...props} />
}

export { TabGroup, TabGroupList, TabGroupTrigger, TabGroupContent }
export type { TabGroupSize, TabGroupColor }
